import { z } from 'zod';
import { moderationStateSchema, triggerLanguageSchema, triggerMatchTypeSchema, triggerSeveritySchema, } from './enums';
export const triggerWordRuleSchema = z.object({
    id: z.string(),
    pattern: z.string(),
    matchType: triggerMatchTypeSchema,
    severity: triggerSeveritySchema,
    language: triggerLanguageSchema,
    isAllowList: z.boolean(),
    isActive: z.boolean(),
    effectiveFrom: z.coerce.date(),
    createdAt: z.coerce.date(),
});
export const createTriggerWordRuleSchema = z.object({
    pattern: z.string().trim().min(1, 'Pattern is required').max(256),
    matchType: triggerMatchTypeSchema.default('Word'),
    severity: triggerSeveritySchema.default('Flag'),
    language: triggerLanguageSchema.default('en'),
    isAllowList: z.boolean().default(false),
});
/**
 * `pattern` is deliberately absent — editing the text of a live rule out from
 * under a moderator mid-review is more confusing than deactivating it and
 * creating a replacement, the same reasoning `updateCategorySchema` gives for
 * leaving `key` out.
 */
export const updateTriggerWordRuleSchema = z
    .object({
    severity: triggerSeveritySchema.optional(),
    isActive: z.boolean().optional(),
})
    .refine((patch) => Object.keys(patch).length > 0, {
    message: 'Provide at least one field to update',
});
/**
 * Seeded on boot, insert-only, exactly like DEFAULT_CATEGORIES — see
 * TriggerWordBootstrapService. Deliberately narrow: only content that is
 * safe and non-sensitive to have sitting in source control. Regex patterns
 * below are deliberately simple shape-checks (digit counts, email shape)
 * rather than full validation (e.g. an SA ID checksum) — the point is to
 * flag for human review, not to definitively classify.
 *
 * The requirements doc's "High-Risk (Always Flag for Review)" trigger
 * categories are Profanity & Offensive Language, Harassment & Abuse,
 * Discriminatory Language, and Threats & Aggression — but the doc itself
 * only gives concrete example phrases for two of those (Harassment & Abuse,
 * Threats & Aggression), which are seeded below as Flag severity, per the
 * doc's own heading. Profanity & Offensive Language and Discriminatory
 * Language have no concrete words in the doc either — deliberately, the same
 * reason this file doesn't invent its own list: real profanity/slur content
 * has no business sitting in a public git diff. Those two categories are
 * administrator-maintained, added through the trigger-rule admin screen,
 * per prisma/sql/002-reference-data.sql's own header comment.
 */
export const DEFAULT_TRIGGER_RULES = [
    // POPIA-shaped personal information — held for review (Block), never just
    // logged, because this is the one category where "publish now, review
    // later" is the wrong default.
    {
        id: 'twr_block_sa_id_number',
        pattern: '\\b\\d{13}\\b',
        matchType: 'Regex',
        severity: 'Block',
        language: 'en',
        isAllowList: false,
    },
    {
        id: 'twr_block_phone_number',
        pattern: '(?:\\+27|0)[6-8]\\d{8}\\b',
        matchType: 'Regex',
        severity: 'Block',
        language: 'en',
        isAllowList: false,
    },
    {
        id: 'twr_block_email_address',
        pattern: '[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}',
        matchType: 'Regex',
        severity: 'Block',
        language: 'en',
        isAllowList: false,
    },
    {
        id: 'twr_block_bank_account_number',
        pattern: '\\b\\d{9,11}\\b',
        matchType: 'Regex',
        severity: 'Block',
        language: 'en',
        isAllowList: false,
    },
    {
        // Passport number — the one POPIA item from the doc's list this was
        // still missing. Shape-only, same restraint as the others: a letter
        // followed by 6-9 digits covers the common SA and international format
        // without pretending to validate a specific country's document.
        id: 'twr_block_passport_number',
        pattern: '\\b[A-Za-z]\\d{6,9}\\b',
        matchType: 'Regex',
        severity: 'Block',
        language: 'en',
        isAllowList: false,
    },
    // Support-shaped language — this is not a support channel, so these
    // publish immediately (Flag, not Block) and are queued for review; the
    // poster also gets a separate inline redirect notice (not part of this
    // list — see SUPPORT_NOTICE_PHRASES in the API's moderation service).
    ...[
        'urgent',
        'escalation',
        'ticket',
        'incident',
        'system down',
        'not working',
        'unable to log in',
        'ticket number',
        'service request',
        'technical problem',
    ].map((pattern, index) => ({
        id: `twr_flag_support_${index}`,
        pattern,
        matchType: pattern.includes(' ') ? 'Phrase' : 'Word',
        severity: 'Flag',
        language: 'en',
        isAllowList: false,
    })),
    // Reputational-risk language — publishes immediately, queued for review.
    ...['scam', 'unethical', 'corruption', 'misleading', 'false advertising'].map((pattern, index) => ({
        id: `twr_flag_reputational_${index}`,
        pattern,
        matchType: pattern.includes(' ') ? 'Phrase' : 'Word',
        severity: 'Flag',
        language: 'en',
        isAllowList: false,
    })),
    // Harassment & Abuse — the requirements doc's own worked examples under
    // "High-Risk (Always Flag for Review)". Flag, not Block, per that heading:
    // publishes immediately, queued for review, same as support/reputational
    // language above.
    ...['idiot', 'stupid', 'moron', 'shut up', "you're useless"].map((pattern, index) => ({
        id: `twr_flag_harassment_${index}`,
        pattern,
        matchType: pattern.includes(' ') ? 'Phrase' : 'Word',
        severity: 'Flag',
        language: 'en',
        isAllowList: false,
    })),
    // Threats & Aggression — same doc section, same reasoning as Harassment &
    // Abuse above.
    ...['i\'ll report you', 'i\'ll make sure you\'re fired'].map((pattern, index) => ({
        id: `twr_flag_threat_${index}`,
        pattern,
        matchType: 'Phrase',
        severity: 'Flag',
        language: 'en',
        isAllowList: false,
    })),
];
/** What the moderation queue shows — enough context to review without opening the full thread. */
export const moderationQueueItemSchema = z.object({
    targetType: z.enum(['Topic', 'Comment']),
    targetId: z.string(),
    topicId: z.string(),
    excerpt: z.string(),
    authorDisplayName: z.string(),
    matchedPatterns: z.array(z.string()),
    /** Every reason a member gave when reporting this item — empty if nobody has. */
    reportReasons: z.array(z.string()),
    createdAt: z.coerce.date(),
    /**
     * PendingReview: held, invisible to everyone but its author until a
     * moderator decides. Published: a Flag-severity match (or a report on
     * content that was already published) — visible to everyone right now,
     * just waiting on review. Reject on a Published item takes something
     * currently visible down; on a PendingReview item it just keeps it hidden.
     */
    moderationState: moderationStateSchema,
});
/** A member flagging a topic/comment for a moderator to look at. */
export const reportItemSchema = z.object({
    reason: z.string().trim().min(1, 'Reason is required').max(1000),
});
//# sourceMappingURL=moderation.js.map